import { type RefObject, useCallback, useState } from 'react'
import type { VirtuosoHandle } from 'react-virtuoso'

import type { ChatItem } from './chat-message.interface'
import { useStreamingAutoScroll } from './use-streaming-auto-scroll.hook'

interface UseChatScrollBehaviorParams {
  virtuosoRef: RefObject<VirtuosoHandle | null>
  itemList: ChatItem[]
  isFollowOutputEnabled: boolean
}

export const useChatScrollBehavior = ({
  virtuosoRef,
  itemList,
  isFollowOutputEnabled,
}: UseChatScrollBehaviorParams) => {
  const [showScrollButton, setShowScrollButton] = useState(false)

  const { handleAtBottomStateChange: handleStreamingScroll } = useStreamingAutoScroll({
    virtuosoRef,
    itemList,
    isEnabled: isFollowOutputEnabled,
  })

  const handleAtBottomStateChange = useCallback(
    (atBottom: boolean) => {
      setShowScrollButton(!atBottom)
      handleStreamingScroll(atBottom)
    },
    [handleStreamingScroll]
  )

  const scrollToBottom = useCallback(() => {
    virtuosoRef.current?.scrollToIndex({ index: 'LAST', align: 'end', behavior: 'smooth' })
  }, [])

  const handleFollowOutput = useCallback(
    (isCurrentlyAtBottom: boolean): 'smooth' | false =>
      isFollowOutputEnabled && isCurrentlyAtBottom ? 'smooth' : false,
    [isFollowOutputEnabled]
  )

  return {
    showScrollButton,
    handleAtBottomStateChange,
    scrollToBottom,
    handleFollowOutput,
  }
}
